// src/components/ChartCard.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import {
  usePdfExport,
  usePdfExportSection,
} from "../../context/PdfExportContext";
import captureElementForPdf from "../../utils/captureElementForPdf";
import InfoTooltip from "./InfoTooltip";
import "./ChartCard.css";

const FULLSCREEN_FONT_SCALE = 1.35;
const FULLSCREEN_MIN_FONT_SIZE = 13;
const FULLSCREEN_MAX_FONT_SIZE = 22;

const parseFontSize = (value, fallbackPx = 12) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallbackPx;
};

const scalePxValue = (value, fallbackPx = 12) => {
  const scaled = parseFontSize(value, fallbackPx) * FULLSCREEN_FONT_SCALE;
  const clamped = Math.min(FULLSCREEN_MAX_FONT_SIZE, Math.max(FULLSCREEN_MIN_FONT_SIZE, scaled));
  return `${Math.round(clamped * 10) / 10}px`;
};

const scaleTypographyObject = (input) => {
  if (!input || typeof input !== "object") {
    return input;
  }

  if (Array.isArray(input)) {
    return input.map((entry) => scaleTypographyObject(entry));
  }

  const output = { ...input };
  if (Object.prototype.hasOwnProperty.call(output, "fontSize")) {
    output.fontSize = scalePxValue(output.fontSize);
  }
  if (Object.prototype.hasOwnProperty.call(output, "lineHeight")) {
    output.lineHeight = scalePxValue(output.lineHeight, 16);
  }

  Object.keys(output).forEach((key) => {
    const value = output[key];
    if (value && typeof value === "object") {
      output[key] = scaleTypographyObject(value);
    }
  });

  return output;
};

const applyFullscreenTypography = (options) => {
  const typed = scaleTypographyObject(options || {});
  const xaxis = typed?.xaxis || {};
  const yaxis = typed?.yaxis;

  return {
    ...typed,
    xaxis: {
      ...xaxis,
      labels: {
        ...(xaxis.labels || {}),
        style: {
          ...(xaxis.labels?.style || {}),
          fontSize: scalePxValue(xaxis.labels?.style?.fontSize, 12),
        },
      },
    },
    yaxis: Array.isArray(yaxis)
      ? yaxis.map((axis = {}) => ({
          ...axis,
          labels: {
            ...(axis.labels || {}),
            style: {
              ...(axis.labels?.style || {}),
              fontSize: scalePxValue(axis.labels?.style?.fontSize, 12),
            },
          },
        }))
      : {
          ...(yaxis || {}),
          labels: {
            ...(yaxis?.labels || {}),
            style: {
              ...(yaxis?.labels?.style || {}),
              fontSize: scalePxValue(yaxis?.labels?.style?.fontSize, 12),
            },
          },
        },
    legend: {
      ...(typed.legend || {}),
      fontSize: scalePxValue(typed.legend?.fontSize, 13),
    },
    dataLabels: {
      ...(typed.dataLabels || {}),
      style: {
        ...(typed.dataLabels?.style || {}),
        fontSize: scalePxValue(typed.dataLabels?.style?.fontSize, 12),
      },
    },
    tooltip: {
      ...(typed.tooltip || {}),
      style: {
        ...(typed.tooltip?.style || {}),
        fontSize: scalePxValue(typed.tooltip?.style?.fontSize, 13),
      },
    },
  };
};

function ChartWithSideContent({ sideContent, height, children }) {
  const mainRef = useRef(null);
  const [plotInsets, setPlotInsets] = useState({ top: 25, bottom: 69 });

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) {
      return undefined;
    }

    let frameId;
    const syncInsets = () => {
      frameId = requestAnimationFrame(() => {
        const grid = main.querySelector(".apexcharts-grid");
        if (!grid) {
          return;
        }
        const mainRect = main.getBoundingClientRect();
        const gridRect = grid.getBoundingClientRect();
        const next = {
          top: Math.max(0, Math.round(gridRect.top - mainRect.top)),
          bottom: Math.max(0, Math.round(mainRect.bottom - gridRect.bottom)),
        };
        setPlotInsets((current) =>
          current.top === next.top && current.bottom === next.bottom ? current : next
        );
      });
    };

    syncInsets();
    const resizeObserver = new ResizeObserver(syncInsets);
    resizeObserver.observe(main);
    const mutationObserver = new MutationObserver(syncInsets);
    mutationObserver.observe(main, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [height]);

  return (
    <div
      className="chart-card__chart-layout"
      style={{
        height,
        "--chart-side-plot-top": `${plotInsets.top}px`,
        "--chart-side-plot-bottom": `${plotInsets.bottom}px`,
      }}
    >
      <div className="chart-card__chart-side">{sideContent}</div>
      <div ref={mainRef} className="chart-card__chart-main">{children}</div>
    </div>
  );
}

function ChartCard({
  title,
  options,
  series,
  type = "line",
  height = 300,
  info,
  chartKey,
  containerClassName = "",
  chartSideContent = null,
  headerActions = null,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const baseChartIdRef = useRef(null);
  const fullscreenChartIdRef = useRef(null);
  const cardRef = useRef(null);
  const pdfExport = usePdfExport();
  const pdfSection = usePdfExportSection();

  useEffect(() => {
    if (!isFullscreen || typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  const closeFullscreen = () => setIsFullscreen(false);
  const sanitizedTitle = typeof title === "string" && title.trim().length > 0 ? title.trim() : "chart";
  const kebabTitle = sanitizedTitle.replace(/\s+/g, "-").toLowerCase();

  if (!baseChartIdRef.current) {
    baseChartIdRef.current = `${kebabTitle || "chart"}-base-${Math.random().toString(36).slice(2, 9)}`;
  }

  if (!fullscreenChartIdRef.current) {
    fullscreenChartIdRef.current = `${kebabTitle || "chart"}-fullscreen-${Math.random()
      .toString(36)
      .slice(2, 9)}`;
  }

  const baseChartOptions = useMemo(() => {
    const chartConfig = options?.chart || {};
    return {
      ...(options || {}),
      chart: {
        ...chartConfig,
        id: baseChartIdRef.current,
      },
    };
  }, [options]);
  const baseChartKey = chartKey ? `${chartKey}-base` : undefined;
  const fullscreenChartKey = chartKey ? `${chartKey}-fullscreen` : undefined;

  const mergeOptions = (base = {}, overrides = {}) => ({
    ...base,
    ...overrides,
    chart: { ...(base.chart || {}), ...(overrides.chart || {}) },
    plotOptions: { ...(base.plotOptions || {}), ...(overrides.plotOptions || {}) },
    xaxis: { ...(base.xaxis || {}), ...(overrides.xaxis || {}) },
    yaxis: { ...(base.yaxis || {}), ...(overrides.yaxis || {}) },
    legend: { ...(base.legend || {}), ...(overrides.legend || {}) },
    grid: { ...(base.grid || {}), ...(overrides.grid || {}) },
    dataLabels: { ...(base.dataLabels || {}), ...(overrides.dataLabels || {}) },
  });

  const fullscreenChartOptions = useMemo(() => {
    const chartConfig = options?.chart || {};
    const isRadar = type === "radar";
    const baseRadarSize = options?.plotOptions?.radar?.size;
    const fullscreenRadarSize = isRadar
      ? Math.max(Number(baseRadarSize) || 0, 280)
      : undefined;
    const needsLegendSpace = options?.legend?.position === "bottom";
    const baseGridPadding = options?.grid?.padding || {};
    const customFullscreenBottom = Number(options?.fullscreenGridPaddingBottom);
    const extraBottomPadding = Number.isFinite(customFullscreenBottom)
      ? customFullscreenBottom
      : needsLegendSpace
        ? 64
        : 0;
    const fullscreenGridPadding = needsLegendSpace
      ? {
          ...baseGridPadding,
          bottom: Math.max(Number(baseGridPadding?.bottom) || 0, extraBottomPadding),
        }
      : baseGridPadding;
    const fullscreenBase = {
      ...(options || {}),
      chart: {
        ...chartConfig,
        id: fullscreenChartIdRef.current,
      },
      grid: fullscreenGridPadding,
      ...(isRadar
        ? {
            plotOptions: {
              ...(options?.plotOptions || {}),
              radar: {
                ...(options?.plotOptions?.radar || {}),
                size: fullscreenRadarSize,
              },
            },
          }
        : {}),
    };
    const merged = options?.fullscreenOverrides
      ? mergeOptions(fullscreenBase, options.fullscreenOverrides)
      : fullscreenBase;
    return options?.fullscreenKeepBaseTypography
      ? merged
      : applyFullscreenTypography(merged);
  }, [options, type]);

  const waitForChartInstance = (chartId) =>
    new Promise((resolve, reject) => {
      const start = performance.now();

      const tick = () => {
        const chartInstance = ApexCharts.getChartByID(chartId);
        if (chartInstance?.w?.globals?.dom?.elWrap) {
          resolve(chartInstance);
          return;
        }

        if (performance.now() - start > 2000) {
          reject(new Error("Chart instance not ready"));
          return;
        }

        requestAnimationFrame(tick);
      };

      tick();
    });

  const waitForFullscreenChart = () => waitForChartInstance(fullscreenChartIdRef.current);

  const getChartImage = async (chartId, scale = 2) => {
    const chartInstance = await waitForChartInstance(chartId);
    const { imgURI } = await chartInstance.dataURI({ scale });
    const chartWidth =
      chartInstance.w?.globals?.svgWidth ||
      chartInstance.w?.globals?.dom?.elWrap?.offsetWidth ||
      chartInstance.el?.offsetWidth ||
      800;
    const chartHeight =
      chartInstance.w?.globals?.svgHeight ||
      chartInstance.w?.globals?.dom?.elWrap?.offsetHeight ||
      chartInstance.el?.offsetHeight ||
      450;

    return {
      src: imgURI,
      width: chartWidth,
      height: chartHeight,
    };
  };

  const exportFullscreenChartAsPng = async () => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const fullscreenImage = await getChartImage(fullscreenChartIdRef.current, 3);
      const link = document.createElement("a");
      link.href = fullscreenImage.src;
      link.download = `${kebabTitle || "chart"}.png`;
      link.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Kan grafiek niet exporteren als PNG:", error);
    }
  };

  const renderChart = ({ fullscreen = false } = {}) => {
    const chart = (
      <Chart
        key={fullscreen ? fullscreenChartKey : baseChartKey}
        options={fullscreen ? fullscreenChartOptions : baseChartOptions}
        series={series}
        type={type}
        height={fullscreen ? "100%" : height}
      />
    );

    if (!chartSideContent) {
      return chart;
    }

    return (
      <ChartWithSideContent
        sideContent={chartSideContent}
        height={fullscreen ? "100%" : height}
      >
        {chart}
      </ChartWithSideContent>
    );
  };

  const maybeRenderPortal = () => {
    if (!isFullscreen || typeof document === "undefined") {
      return null;
    }

    return createPortal(
      <div className="chart-card-fullscreen" role="dialog" aria-modal="true" onClick={closeFullscreen}>
        <div
          className={`chart-card-fullscreen__content ${containerClassName}`.trim()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="chart-card__header">
            <div className="chart-title-with-info">
              <h3 className="chart-title">{title}</h3>
              {info && <InfoTooltip text={info} label={`Informatie over ${title}`} />}
            </div>
            <div className="chart-card__actions">
              <button type="button" className="chart-card__action" onClick={exportFullscreenChartAsPng}>
                Export als PNG
              </button>
              <button type="button" className="chart-card__action" onClick={closeFullscreen}>
                Sluiten
              </button>
            </div>
          </div>
          <div className="chart-card-fullscreen__body">
            <div className="chart-card-fullscreen__chart">
              {renderChart({ fullscreen: true })}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  useEffect(() => {
    if (!pdfExport?.registerExportable) {
      return undefined;
    }

    const unregister = pdfExport.registerExportable({
      sectionKey: pdfSection?.sectionKey,
      order: pdfSection?.order,
      getContent: async () => {
        if (typeof window === "undefined") {
          return null;
        }

        try {
          if (pdfSection?.sectionKey && cardRef.current) {
            const canvas = await captureElementForPdf(cardRef.current, {
              hideSelectors: [".chart-card__header"],
              ignoreActions: true,
            });
            return {
              title: sanitizedTitle,
              image: {
                src: canvas.toDataURL("image/png"),
                width: canvas.width,
                height: canvas.height,
              },
            };
          }
          const image = await getChartImage(baseChartIdRef.current, 2);
          return {
            title: sanitizedTitle,
            image,
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Kan grafiek niet exporteren naar dashboard PDF:", error);
          return null;
        }
      },
    });

    return unregister;
  }, [pdfExport, pdfSection, sanitizedTitle]);

  return (
    <>
      <div ref={cardRef} className={`chart-card ${containerClassName}`.trim()}>
        <div className="chart-card__header">
          <div className="chart-title-with-info">
            <h3 className="chart-title">{title}</h3>
            {info && <InfoTooltip text={info} label={`Informatie over ${title}`} />}
          </div>
          <div className="chart-card__actions">
            <button
              type="button"
              className="chart-card__action"
              onClick={() => setIsFullscreen(true)}
              aria-pressed={isFullscreen}
            >
              Volledig scherm
            </button>
            {headerActions}
          </div>
        </div>
        <div className="chart-card__body">
          {renderChart()}
        </div>
      </div>
      {maybeRenderPortal()}
    </>
  );
}

export default ChartCard;
